import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarIcon, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SidePanel } from "@/components/ui/side-panel";
import { CohortsKeys, createCohort } from "@/features/cohorts/api/cohorts";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  startDate: z.date({
    required_error: "Start date is required",
  }),
});

type FormValues = z.infer<typeof formSchema>;

interface NewCohortDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const NewCohortDrawer: React.FC<NewCohortDrawerProps> = ({ open, setOpen }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [calendarOpen, setCalendarOpen] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: undefined,
    },
  });

  // Create Cohort
  const { mutate, isPending } = useMutation({
    mutationFn: createCohort,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CohortsKeys.GET_COHORTS],
      });
      setOpen(false);
      form.reset();
      toast({ title: "Cohort created successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating cohort",
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      setCalendarOpen(false);
    }
  }, [open, form]);

  const onSubmit = (values: FormValues) => {
    // Format date as ISO string for API
    mutate({
      startDate: values.startDate.toISOString(),
    });
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return null;
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(false)}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="cohort-form"
        disabled={isPending || !form.formState.isValid}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          "Create"
        )}
      </Button>
    </div>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={(isOpen) => setOpen(isOpen)}
      title="Create New Cohort"
      subtitle="Add a new cohort to the system"
      footer={footerContent}
    >
      <Form {...form}>
        <form
          id="cohort-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>
                  Start Date <span className="text-destructive">*</span>
                </FormLabel>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !field.value && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value
                          ? formatDate(field.value)
                          : "Pick a start date"}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setCalendarOpen(false);
                      }}
                      initialFocus
                      disabled={(date) =>
                        date < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  Select the date when the cohort will start
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </SidePanel>
  );
};

export default NewCohortDrawer;
